import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import type { AuditLogRow } from '@shared/types/api';
import {
  getActionBadgeTone,
  formatFullDateTime,
  formatRelativeTime,
  formatUserAgent,
} from './audit-log-utils';
import { analyzeAuditEvent } from './audit-log-semantic-diff';
import { AuditLogChangesView } from './audit-log-changes-view';
import { JsonDiffViewer } from './audit-log-diff-viewer';

export function AuditLogDetailDialog({
  log,
  onClose,
}: {
  log: AuditLogRow | null;
  onClose: () => void;
}) {
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  if (!log) return null;

  const analysis = analyzeAuditEvent(log);
  const badge = getActionBadgeTone(log.action);

  const actorFullName = log.actor
    ? [log.actor.firstName, log.actor.lastName].filter(Boolean).join(' ')
    : '';
  const actorEmail = log.actor?.email || null;

  // Resolve target entity primary name and email/subtext cleanly
  const targetResolved = log.entityId ? log.resolvedEntities?.[log.entityId] : null;
  let targetName = targetResolved?.label || log.entityLabel || log.entityType || '—';
  let targetEmail: string | null = targetResolved?.subtext || null;

  // Fallback: If entityLabel was formatted as "Name (email)", parse them so email is displayed in full on its own line
  if (!targetEmail && log.entityLabel) {
    const match = log.entityLabel.match(/^(.*?)\s*\((.*?)\)$/);
    if (match) {
      targetName = match[1];
      targetEmail = match[2];
    }
  }

  const handleCopyId = () => {
    navigator.clipboard.writeText(log.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  return (
    <Dialog open onClose={onClose} className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0" closeOnOverlayClick>
      {/* Section A: Header */}
      <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between bg-white shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-bold text-gray-900 leading-tight">
                {analysis.title}
              </h3>
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border shadow-2xs font-mono',
                  badge.bg,
                  badge.text,
                )}
              >
                <span className={cn('w-1.5 h-1.5 rounded-full', badge.dot)} />
                {log.action}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-gray-400 font-medium">Log ID:</span>
              <button
                type="button"
                onClick={handleCopyId}
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-mono text-[11px] transition-colors cursor-pointer group"
                title="Click to copy full Audit Log ID"
              >
                <span>{log.id}</span>
                {copiedId ? (
                  <span className="text-emerald-600 font-semibold text-[10px]">Copied!</span>
                ) : (
                  <svg className="w-3 h-3 text-gray-400 group-hover:text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0 ml-4">
          <button
            type="button"
            onClick={onClose}
            className="-mr-1.5 rounded-lg p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-hidden cursor-pointer"
            title="Close dialog (Esc)"
            aria-label="Close dialog"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Main Body - Scrollable */}
      <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
        {/* Section B: Activity Summary Banner */}
        <div className="rounded-xl border border-brand-200/80 bg-brand-50/50 p-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-900 font-bold text-xs">
              ℹ
            </div>
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-brand-800 block mb-0.5">
                Activity Summary
              </span>
              <p className="text-sm font-semibold text-gray-900 leading-snug">
                {analysis.summary}
              </p>
            </div>
          </div>
        </div>

        {/* Structured Context Metadata Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 bg-gray-50/70 p-4 rounded-xl border border-gray-200/70 text-xs">
          {/* Actor */}
          <div>
            <span className="text-gray-500 font-medium block mb-1">Actor</span>
            {actorFullName ? (
              <div>
                <span className="font-bold text-gray-900 block leading-tight">
                  {actorFullName}
                </span>
                {actorEmail && (
                  <span className="text-gray-600 font-mono text-[11px] block break-all leading-snug mt-0.5 select-all" title={actorEmail}>
                    {actorEmail}
                  </span>
                )}
              </div>
            ) : actorEmail ? (
              <span className="font-bold font-mono text-gray-900 block break-all leading-snug select-all" title={actorEmail}>
                {actorEmail}
              </span>
            ) : (
              <span className="font-bold text-gray-900 block">System Actor</span>
            )}
            <div className="mt-1.5">
              {log.actor?.adminRole ? (
                <span className="inline-block text-[10px] text-brand-800 bg-brand-100 border border-brand-200/70 px-2 py-0.5 rounded font-semibold tracking-wide">
                  {log.actor.adminRole}
                </span>
              ) : (
                <span className="inline-block text-[10px] text-gray-600 bg-gray-200/70 px-2 py-0.5 rounded font-medium">
                  System
                </span>
              )}
            </div>
          </div>

          {/* Timestamp */}
          <div>
            <span className="text-gray-500 font-medium block mb-1">Timestamp</span>
            <span className="font-bold text-gray-900 block whitespace-nowrap">
              {formatFullDateTime(log.createdAt)}
            </span>
            <span className="text-[11px] text-gray-500 block mt-0.5">
              ({formatRelativeTime(log.createdAt)})
            </span>
          </div>

          {/* Target Entity */}
          <div>
            <span className="text-gray-500 font-medium block mb-1">Target Entity</span>
            <div>
              <span className="font-bold text-gray-900 block leading-tight break-words" title={targetName}>
                {targetName}
              </span>
              {targetEmail && (
                <span className="text-gray-600 font-mono text-[11px] block break-all leading-snug mt-0.5 select-all" title={targetEmail}>
                  {targetEmail}
                </span>
              )}
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                <span className="text-[10px] text-brand-800 bg-brand-50 border border-brand-200 px-1.5 py-0.2 rounded font-semibold">
                  {targetResolved?.type || log.entityType || 'Record'}
                </span>
                {log.entityId && (
                  <span className="font-mono text-[10px] text-gray-400" title={log.entityId}>
                    #{log.entityId.slice(0, 8)}...
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* IP Address & Device */}
          <div>
            <span className="text-gray-500 font-medium block mb-1">Network & Device</span>
            <span className="font-mono font-bold text-gray-900 block select-all">
              {log.ipAddress || 'Not recorded'}
            </span>
            <span className="text-[11px] text-gray-600 block mt-0.5 truncate" title={log.userAgent || ''}>
              {formatUserAgent(log.userAgent) || (log.userAgent ? log.userAgent.split(' ')[0] : 'Device not recorded')}
            </span>
            {log.ipAddress && (
              <span className="inline-block mt-1.5 text-[9px] uppercase tracking-wider text-gray-600 bg-gray-100 border border-gray-200 px-1.5 py-0.2 rounded font-medium">
                Recorded IP
              </span>
            )}
          </div>
        </div>

        {/* Section C: Changes Made */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                Changes Made
              </h4>
              {analysis.changes.length > 0 && (
                <span className="rounded-full bg-brand-100 text-brand-900 font-bold px-2 py-0.2 text-[10px] border border-brand-200">
                  {analysis.changes.length} {analysis.changes.length === 1 ? 'change' : 'changes'}
                </span>
              )}
            </div>
            <span className="text-[11px] text-gray-400">
              Computed semantic differences
            </span>
          </div>

          <AuditLogChangesView
            changes={analysis.changes}
            noChangesReason={analysis.noChangesReason}
            isPermissionEvent={log.action === 'admin_permissions.updated'}
          />
        </div>

        {/* Section D: Collapsible Technical Details (Raw Snapshots & Copy JSON) */}
        <div className="rounded-xl border border-gray-200 bg-gray-50/50 overflow-hidden">
          <button
            type="button"
            onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-100/60 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <svg
                className={cn('w-4 h-4 text-gray-500 transition-transform duration-200', showTechnicalDetails && 'rotate-90')}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                Technical Details (Raw State Snapshots)
              </span>
            </div>
            <span className="text-[11px] text-brand-800 font-medium">
              {showTechnicalDetails ? 'Collapse raw data' : 'Expand raw JSON & diffs'}
            </span>
          </button>

          {showTechnicalDetails && (
            <div className="p-4 border-t border-gray-200 bg-white">
              <JsonDiffViewer before={log.before} after={log.after} resolvedEntities={log.resolvedEntities} />
            </div>
          )}
        </div>
      </div>

      {/* Section E: Footer */}
      <div className="border-t border-gray-200 px-6 py-3.5 flex justify-end bg-gray-50/50 shrink-0">
        <Button variant="outline" className="px-4 py-1.5 text-xs font-medium cursor-pointer" onClick={onClose}>
          Close
        </Button>
      </div>
    </Dialog>
  );
}
